import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Tier {
  id: "starter" | "pro";
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  highlighted?: boolean;
}

const tiers: Tier[] = [
  {
    id: "starter",
    name: "Starter",
    price: "$8",
    period: "/ month",
    description: "For the solo stylist running a tight, focused archive.",
    features: [
      "Item cataloging with photo upload & custom fields",
      "Brand, size, color, condition, material, client",
      "Location tracking with full movement history",
      "Advanced search and filtering",
      "Basic client profiles",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: "$90",
    period: "/ month",
    description:
      "For studios, teams, and managers running multiple clients across multiple locations.",
    highlighted: true,
    features: [
      "Everything in Starter",
      "Barcode / QR code generation and scanning",
      "Multi-location: racks, bins, archives, residences",
      "Outfit creation & lookbook for fittings and shoots",
      "Loan / borrow tracking with return dates",
      "Condition tracking & maintenance notes",
      "Reporting: most-used items, inventory value, seasonal trends",
    ],
  },
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      aria-labelledby="pricing-heading"
      className="py-20 md:py-28"
    >
      <div className="max-w-[1320px] mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-14 md:mb-20">
          <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-5">
            — Pricing
          </p>
          <h2
            id="pricing-heading"
            className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-balance"
          >
            Two plans. Both start with{" "}
            <span className="italic text-oxblood">14 days free.</span>
          </h2>
          <p className="mt-5 text-sm md:text-base text-muted-foreground">
            No credit card required. Cancel anytime. Upgrade the moment your
            archive outgrows you.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              data-testid={`pricing-card-${tier.id}`}
              className={`relative flex flex-col p-8 md:p-10 border ${
                tier.highlighted
                  ? "border-oxblood bg-background shadow-[0_24px_80px_-32px_rgba(107,31,43,0.35)]"
                  : "border-border bg-background"
              }`}
            >
              {tier.highlighted && (
                <span
                  data-testid="pricing-badge-popular"
                  className="absolute -top-3 left-8 inline-block bg-oxblood text-ivory text-[10px] tracking-[0.25em] uppercase font-medium px-3 py-1.5"
                >
                  Most popular
                </span>
              )}

              <div className="flex items-baseline justify-between">
                <h3 className="font-display text-2xl md:text-3xl">
                  {tier.name}
                </h3>
                <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  14-day trial
                </span>
              </div>

              <p className="mt-3 text-sm text-muted-foreground max-w-sm leading-relaxed">
                {tier.description}
              </p>

              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display text-5xl md:text-6xl text-foreground">
                  {tier.price}
                </span>
                <span className="text-sm text-muted-foreground">
                  {tier.period}
                </span>
              </div>

              <ul
                data-testid={`pricing-features-${tier.id}`}
                className="mt-8 space-y-3 flex-1"
              >
                {tier.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-3 text-sm md:text-[15px] text-foreground/85"
                  >
                    <Check
                      className="w-4 h-4 mt-1 shrink-0 text-oxblood"
                      strokeWidth={2}
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                asChild
                size="lg"
                data-testid={`pricing-cta-${tier.id}`}
                className={`mt-10 rounded-none h-12 ${
                  tier.highlighted
                    ? "bg-oxblood hover:bg-oxblood-deep text-ivory"
                    : "bg-foreground hover:bg-charcoal text-ivory"
                }`}
              >
                <Link to={`/signup?plan=${tier.id}`}>
                  Start 14-Day Free Trial
                </Link>
              </Button>

              <p className="mt-3 text-[11px] tracking-wide text-muted-foreground text-center">
                No credit card required
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
